/* --------------------------------------------------------------------------
   Single source of truth for per-route SEO.

   Read twice:
     - at runtime by src/seo.js (useSeo), so client-side route changes update
       the <head>;
     - at build time by scripts/prerender.js, which bakes these values into the
       static HTML for each route.

   Keep the keys in sync with the routes in src/App.js and the URLs in
   public/sitemap.xml.
   -------------------------------------------------------------------------- */

export const SITE_URL = 'https://mantlebudget.com';
export const SITE_NAME = 'Mantle';

/* The 1200x630 card written by scripts/make-icons.ps1. Deliberately NOT the raw
   logo: Google and every social scraper expect 1.91:1 at 1200px wide, and an
   image at any other ratio is either cropped, shrunk to a square thumbnail or
   dropped. `max-image-preview:large` (set in public/index.html) is what gives
   Google permission to show it at full size. */
export const OG_IMAGE = `${SITE_URL}/og-card.png`;
export const OG_IMAGE_ALT = 'Mantle — budgeting and forecasting on top of your ERP';

/** Titles under ~60 chars and descriptions under ~155 chars, or Google truncates them. */
export const PAGE_SEO = {
  '/': {
    title: 'Mantle — Budgeting & Forecasting on Top of Your ERP',
    description:
      'The budgeting, forecasting and LE planning layer that sits on top of your ERP — Price × Quantity budgets for FMCG, manufacturing, retail and construction.',
  },
  '/platform': {
    title: 'The Mantle Platform — Nine Interoperable Budgeting Modules',
    description:
      'Activate only the modules you need — OPEX, supply chain, production, distribution, CAPEX, revenue, reporting, paycost and planning — on one budget model.',
  },
  '/services': {
    title: 'Mantle Services — Budgeting Deployment & Implementation Support',
    description:
      'Mantle deploys with the people who run budgets for a living: 15+ years of multi-national budgeting, hands-on configuration, changes in days not weeks.',
  },
  '/industries': {
    title: 'Budgeting Software for Manufacturing, Mining, Distribution & Retail',
    description:
      'Mantle mirrors the physical supply chain: BOMs for manufacturing, paycost for mining, lane costing for distribution, shelf pricing for retail.',
  },
  '/pricing': {
    title: 'Mantle Pricing — Priced by Modules and Sites, Not Per User',
    description:
      'No per-seat licensing. You pay for the sites and modules you activate, so everyone who touches the budget can use Mantle without a seat count getting in the way.',
  },
  '/about': {
    title: 'About Mantle — Budgeting Built by Finance Operators',
    description:
      'Mantle was built by finance people who ran Budget, PF and LE cycles inside large multi-national businesses — and wanted margin visibility down to SKU level.',
  },
};

/* Real routes that are deliberately kept out of search results (they are also
   Disallowed in public/robots.txt). They still get prerendered: without their
   own HTML they would fall back to the home page's markup and hydrate into a
   mismatch. */
export const NOINDEX_ROUTES = ['/login', '/domain'];
